import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Send, Loader2, Mail, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const fromServer = (data) => ({
    ...DEFAULTS,
    ...data,
    owner_email: data.owner_email || "",
    reply_to: data.reply_to || "",
    exit_discount_ends_at: toLocalInput(data.exit_discount_ends_at),
});

const DEFAULTS = {
    owner_email: "",
    reply_to: "",
    customer_order_email: true,
    customer_shipped_email: true,
    admin_order_email: true,
    show_exit_intent: true,
    exit_discount_code: "SAVE10",
    exit_discount_percent: 10,
    exit_discount_duration_minutes: 15,
    exit_discount_ends_at: "",
    daily_sales_alert_threshold: 10,
    google_site_verification: "",
    online_payment_enabled: true,
    online_payment_discount: 100,
    bank_details: "",
    delivery_fee: 0,
    easypaisa_number: "03327147731",
    easypaisa_title: "Muhammad Azhar",
    jazzcash_number: "03327147731",
    jazzcash_title: "Muhammad Azhar",
    hbl_bank: "HBL",
    hbl_account_title: "Muhammad Azhar",
    hbl_account_number: "26797000008803",
    hbl_iban: "PK31HABB0026797000008803",
};

export default function AdminSettings() {
    const [form, setForm] = useState(DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    useEffect(() => {
        api.get("/admin/email-settings")
            .then((r) => setForm(fromServer(r.data)))
            .finally(() => setLoading(false));
    }, []);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                owner_email: form.owner_email || null,
                reply_to: form.reply_to || null,
                exit_discount_percent: Number(form.exit_discount_percent) || 10,
                exit_discount_code: (form.exit_discount_code || "SAVE10").toUpperCase(),
                online_payment_discount: Number(form.online_payment_discount) || 0,
                bank_details: form.bank_details || "",
                delivery_fee: Number(form.delivery_fee) || 0,
                exit_discount_duration_minutes: Number(form.exit_discount_duration_minutes) || 15,
                exit_discount_ends_at: form.exit_discount_ends_at
                    ? new Date(form.exit_discount_ends_at).toISOString()
                    : null,
                daily_sales_alert_threshold: Number(form.daily_sales_alert_threshold) || 10,
            };
            const { data } = await api.put("/admin/email-settings", payload);
            setForm(fromServer(data));
            toast.success("Settings saved");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            await api.post("/admin/email-settings/test");
            toast.success(`Test email sent to ${form.owner_email}`);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setTesting(false);
        }
    };

    if (loading) return <div className="text-sm text-forest-700/70">Loading…</div>;

    return (
        <div data-testid="admin-settings" className="space-y-6 max-w-3xl">
            <header>
                <h1 className="font-serif text-3xl sm:text-4xl font-bold text-forest-800">
                    Email &amp; Automation Settings
                </h1>
                <p className="text-sm text-forest-900/70 mt-1">
                    Control which transactional emails fire, and the exit-intent discount.
                </p>
            </header>

            <form onSubmit={save} className="space-y-6">
                <section className="bg-white rounded-2xl border border-forest-100 shadow-sm p-5 sm:p-6 space-y-4">
                    <h2 className="font-serif font-bold text-lg text-forest-800 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gold-600" />
                        Sender &amp; recipient
                    </h2>
                    <TextInput
                        label="Owner email (receives new order alerts)"
                        type="email"
                        testid="owner-email"
                        value={form.owner_email}
                        onChange={(v) => update({ owner_email: v })}
                        placeholder="owner@yourdomain.com"
                        hint="Required for admin alerts and the 'Send test' button."
                    />
                    <TextInput
                        label="Reply-to inbox (optional)"
                        type="email"
                        testid="reply-to"
                        value={form.reply_to}
                        onChange={(v) => update({ reply_to: v })}
                        placeholder="support@yourdomain.com"
                        hint="If a customer replies to your emails, replies land here."
                    />
                </section>

                <section className="bg-white rounded-2xl border border-forest-100 shadow-sm p-5 sm:p-6 space-y-3">
                    <h2 className="font-serif font-bold text-lg text-forest-800 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-gold-600" />
                        Automated emails
                    </h2>
                    <Toggle
                        label="Order confirmation email to customer"
                        testid="toggle-customer-order"
                        checked={form.customer_order_email}
                        onChange={(v) => update({ customer_order_email: v })}
                    />
                    <Toggle
                        label="Shipped notification to customer (when status set to 'shipped')"
                        testid="toggle-customer-shipped"
                        checked={form.customer_shipped_email}
                        onChange={(v) => update({ customer_shipped_email: v })}
                    />
                    <Toggle
                        label="New-order alert to owner email"
                        testid="toggle-admin-order"
                        checked={form.admin_order_email}
                        onChange={(v) => update({ admin_order_email: v })}
                    />
                    <div className="pt-1 border-t border-forest-100 flex items-center gap-3">
                        <button
                            type="button"
                            onClick={sendTest}
                            disabled={testing || !form.owner_email}
                            data-testid="send-test-email"
                            className="inline-flex items-center gap-2 bg-forest-700 hover:bg-forest-800 disabled:opacity-60 text-gold-400 font-bold text-xs px-4 py-2.5 rounded-xl border border-gold-500/30"
                        >
                            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            Send test to owner email
                        </button>
                        <span className="text-[11px] text-forest-700/60">
                            Confirms your inbox receives alerts.
                        </span>
                    </div>
                </section>

                <section className="bg-white rounded-2xl border border-forest-100 shadow-sm p-5 sm:p-6 space-y-4">
                    <h2 className="font-serif font-bold text-lg text-forest-800">
                        Exit-intent discount
                    </h2>
                    <Toggle
                        label="Show the exit-intent popup to leaving visitors"
                        testid="toggle-exit-intent"
                        checked={form.show_exit_intent}
                        onChange={(v) => update({ show_exit_intent: v })}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <TextInput
                            label="Discount code"
                            testid="exit-code"
                            value={form.exit_discount_code}
                            onChange={(v) => update({ exit_discount_code: v.toUpperCase() })}
                        />
                        <TextInput
                            label="Discount %"
                            type="number"
                            testid="exit-percent"
                            value={form.exit_discount_percent}
                            onChange={(v) => update({ exit_discount_percent: v })}
                            min={0}
                            max={90}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <TextInput
                            label="Per-visitor timer (minutes)"
                            type="number"
                            testid="exit-duration"
                            value={form.exit_discount_duration_minutes}
                            onChange={(v) => update({ exit_discount_duration_minutes: v })}
                            min={1}
                            max={1440}
                            hint="Countdown starts when the popup appears."
                        />
                        <TextInput
                            label="Campaign end date & time (optional)"
                            type="datetime-local"
                            testid="exit-ends-at"
                            value={form.exit_discount_ends_at}
                            onChange={(v) => update({ exit_discount_ends_at: v })}
                            hint="After this moment the code stops working for everyone."
                        />
                    </div>
                    <p className="text-[11px] text-forest-700/70">
                        Visitors see a live countdown in the popup and at checkout. The code expires at whichever comes first.
                    </p>
                </section>

                <section className="bg-white rounded-2xl border border-forest-100 shadow-sm p-5 sm:p-6 space-y-4">
                    <h2 className="font-serif font-bold text-lg text-forest-800">
                        Low-stock alert
                    </h2>
                    <TextInput
                        label="Email me when one product sells this many units in a day"
                        type="number"
                        testid="sales-alert-threshold"
                        value={form.daily_sales_alert_threshold}
                        onChange={(v) => update({ daily_sales_alert_threshold: v })}
                        min={1}
                        max={1000}
                        hint="Sent once per product per day to the owner email above."
                    />
                </section>

                <section data-testid="seo-section" className="bg-white rounded-2xl border border-forest-100 shadow-sm p-5 sm:p-6 space-y-4">
                    <h2 className="font-serif font-bold text-lg text-forest-800">
                        Google Search Console
                    </h2>
                    <TextInput
                        label="Google site verification code"
                        testid="gsc-verification-input"
                        value={form.google_site_verification}
                        onChange={(v) => update({ google_site_verification: v })}
                        hint='Paste the full <meta name="google-site-verification" ...> tag or just its content code.'
                    />
                    <p className="text-[11px] text-forest-700/70">
                        Sitemap to submit in Search Console: <b data-testid="sitemap-url">{window.location.origin}/api/sitemap.xml</b>
                    </p>
                </section>

                <section className="bg-white rounded-2xl border border-forest-100 shadow-sm p-5 sm:p-6 space-y-4">
                    <h2 className="font-serif font-bold text-lg text-forest-800">
                        Online payment bonus
                    </h2>
                    <Toggle
                        label="Offer customers online-payment methods (Easypaisa / JazzCash / HBL) at checkout"
                        testid="toggle-online-payment"
                        checked={form.online_payment_enabled}
                        onChange={(v) => update({ online_payment_enabled: v })}
                    />
                    <TextInput
                        label="Extra discount for paying online (PKR)"
                        type="number"
                        testid="online-discount-amount"
                        value={form.online_payment_discount}
                        onChange={(v) => update({ online_payment_discount: v })}
                        min={0}
                        max={1000}
                    />
                    <TextInput
                        label="Flat delivery fee (PKR)"
                        type="number"
                        testid="delivery-fee"
                        value={form.delivery_fee}
                        onChange={(v) => update({ delivery_fee: v })}
                        min={0}
                        max={2000}
                        hint="Added to every order's total. Set 0 to make delivery free again."
                    />
                </section>

                <section className="bg-white rounded-2xl border border-forest-100 shadow-sm p-5 sm:p-6 space-y-5">
                    <h2 className="font-serif font-bold text-lg text-forest-800">
                        Payment account details
                    </h2>
                    <p className="text-xs text-forest-900/70 -mt-3">
                        Shown to the customer when they pick each method in the checkout popup.
                    </p>

                    <div className="space-y-3">
                        <h3 className="font-bold text-sm text-forest-800">Easypaisa</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <TextInput
                                label="Account Title"
                                testid="easypaisa-title"
                                value={form.easypaisa_title}
                                onChange={(v) => update({ easypaisa_title: v })}
                            />
                            <TextInput
                                label="Account Number"
                                testid="easypaisa-number"
                                value={form.easypaisa_number}
                                onChange={(v) => update({ easypaisa_number: v })}
                            />
                        </div>
                    </div>

                    <div className="space-y-3 pt-2 border-t border-forest-100">
                        <h3 className="font-bold text-sm text-forest-800">JazzCash</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <TextInput
                                label="Account Title"
                                testid="jazzcash-title"
                                value={form.jazzcash_title}
                                onChange={(v) => update({ jazzcash_title: v })}
                            />
                            <TextInput
                                label="Account Number"
                                testid="jazzcash-number"
                                value={form.jazzcash_number}
                                onChange={(v) => update({ jazzcash_number: v })}
                            />
                        </div>
                    </div>

                    <div className="space-y-3 pt-2 border-t border-forest-100">
                        <h3 className="font-bold text-sm text-forest-800">HBL Bank Transfer</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <TextInput
                                label="Bank"
                                testid="hbl-bank"
                                value={form.hbl_bank}
                                onChange={(v) => update({ hbl_bank: v })}
                            />
                            <TextInput
                                label="Account Title"
                                testid="hbl-title"
                                value={form.hbl_account_title}
                                onChange={(v) => update({ hbl_account_title: v })}
                            />
                            <TextInput
                                label="Account Number"
                                testid="hbl-number"
                                value={form.hbl_account_number}
                                onChange={(v) => update({ hbl_account_number: v })}
                            />
                            <TextInput
                                label="IBAN"
                                testid="hbl-iban"
                                value={form.hbl_iban}
                                onChange={(v) => update({ hbl_iban: v })}
                            />
                        </div>
                    </div>
                </section>

                <section className="bg-forest-50 rounded-2xl border border-forest-100 p-5 text-xs text-forest-900/70 leading-relaxed">
                    <b className="text-forest-800">Note on marketing campaigns:</b> This platform's managed email integration is <b>transactional-only</b>, which rules out bulk promotional blasts. Your opted-in subscribers list is captured on checkout and available on the <b>Subscribers</b> page as a CSV export — pipe it into Mailchimp / Beehiiv / ConvertKit for campaigns.
                </section>

                <button
                    type="submit"
                    data-testid="save-settings"
                    disabled={saving}
                    className="inline-flex items-center gap-2 bg-forest-800 hover:bg-forest-900 disabled:opacity-70 text-gold-400 font-bold text-sm px-6 py-3 rounded-xl border border-gold-500/40"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save settings
                </button>
            </form>
            <ResetDataSection />
        </div>
    );
}

function ResetDataSection() {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const reset = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/reset-data", { confirm: text });
            const d = data.deleted;
            toast.success(`Deleted ${d.orders} orders, ${d.abandoned_carts} abandoned carts, ${d.subscribers} subscribers`);
            setText("");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };
    return (
        <section data-testid="reset-data-section" className="bg-white rounded-2xl border border-rose-200 shadow-sm p-5 sm:p-6 space-y-3">
            <h2 className="font-serif font-bold text-lg text-rose-700">Reset store data</h2>
            <p className="text-xs text-forest-900/70">
                Permanently deletes all orders, abandoned carts, subscribers and sales alerts. Analytics reset to zero. Products and settings are kept. Type <b>DELETE</b> to confirm.
            </p>
            <div className="flex gap-3">
                <input
                    data-testid="reset-confirm-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="DELETE"
                    className="flex-1 bg-forest-50/50 border border-forest-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
                />
                <button
                    type="button"
                    data-testid="reset-data-btn"
                    onClick={reset}
                    disabled={busy || text !== "DELETE"}
                    className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold text-sm px-5 py-2.5 rounded-xl"
                >
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    Delete all data
                </button>
            </div>
        </section>
    );
}

function TextInput({ label, value, onChange, hint, testid, ...props }) {
    return (
        <div>
            <label className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                {label}
            </label>
            <input
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-forest-50/50 border border-forest-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 focus:bg-white"
                {...props}
            />
            {hint && <span className="text-[11px] text-forest-700/70 mt-1 block">{hint}</span>}
        </div>
    );
}

function Toggle({ label, checked, onChange, testid }) {
    return (
        <label className="flex items-start gap-3 bg-forest-50/60 border border-forest-100 rounded-xl px-4 py-3 cursor-pointer">
            <input
                type="checkbox"
                data-testid={testid}
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-forest-700"
            />
            <span className="text-xs font-semibold text-forest-800 leading-snug">
                {label}
            </span>
        </label>
    );
}
